import { z } from "zod";
import { accountDeletionExecutorSafeCodeSchema } from "./account-deletion-executor";
export const accountDeletionSchemaVersion = "account-deletion.v1";
export const accountDeletionUppercaseUlidSchema = z
    .string()
    .length(26)
    .regex(/^[0123456789ABCDEFGHJKMNPQRSTVWXYZ]+$/);
export const accountDeletionHashSchema = z
    .string()
    .length(64)
    .regex(/^[0-9a-f]+$/);
export const accountDeletionIdempotencyKeySchema = z
    .string()
    .trim()
    .min(8)
    .max(128)
    .regex(/^[!-~]+$/);
export const accountDeletionValueDispositionSchema = z.enum([
    "none",
    "refund_review",
    "forfeiture_selection",
]);
export const accountDeletionRequestStatusSchema = z.enum([
    "cooling_off",
    "cancelled",
    "processing",
    "retryable_error",
    "completed",
]);
export const accountDeletionBlockerCodeSchema = accountDeletionExecutorSafeCodeSchema;
export const accountDeletionBlockerSchema = z.strictObject({
    code: accountDeletionBlockerCodeSchema,
    blocksProfileDeactivation: z.boolean(),
    blocksExecution: z.boolean(),
    retryableByExecutor: z.boolean(),
    title: z.string().min(1).max(160).optional(),
    body: z.string().min(1).max(600).optional(),
});
export const accountDeletionValueFactsSchema = z.strictObject({
    activeEntitlementCount: z.number().int().min(0).max(1000),
    scheduledEntitlementCount: z.number().int().min(0).max(1000),
    remainingCredits: z.number().int().min(0).max(9_007_199_254_740_991),
    unusedPaidValue: z.boolean(),
    openCheckoutCount: z.number().int().min(0).max(1000),
    openPaymentCount: z.number().int().min(0).max(1000),
    openRefundCount: z.number().int().min(0).max(1000),
    openDisputeCount: z.number().int().min(0).max(1000),
});
export const accountDeletionLifecycleFactsSchema = z.strictObject({
    legalHoldCount: z.number().int().min(0).max(1000),
    hasAdminRole: z.boolean(),
    lastSuperadmin: z.boolean(),
    transferAssetCount: z.number().int().min(0).max(1000),
    openSupportRequestCount: z.number().int().min(0).max(1000),
    activeAppBan: z.boolean(),
    ownerStatus: z.enum(["active", "deactivated", "pending", "restricted"]),
});
export const accountDeletionExecutorReadinessSchema = z.strictObject({
    tombstoneKeyReady: z.boolean(),
    supportOpsReady: z.boolean(),
    oauthRevocationReady: z.boolean(),
    supportedGraph: z.boolean(),
});
export const accountDeletionPolicyVersionsSchema = z.strictObject({
    decisionLock: z.string().trim().min(1).max(128),
    retention: z.string().trim().min(1).max(128),
    transform: z.string().trim().min(1).max(128),
    valueDisposition: z.string().trim().min(1).max(128),
    supportOperations: z.string().trim().min(1).max(128),
});
export const accountDeletionPreviewCopySchema = z.strictObject({
    title: z.string().min(1).max(200),
    summary: z.string().min(1).max(1000),
    valueDispositionOptions: z
        .array(z.strictObject({
        key: z.enum(["refund_review", "forfeiture_selection"]),
        title: z.string().min(1).max(160),
        body: z.string().min(1).max(600),
    }))
        .max(4),
});
export const accountDeletionPreviewResponseSchema = z.strictObject({
    schemaVersion: z.literal(accountDeletionSchemaVersion),
    locale: z.enum(["id", "en"]),
    generatedAt: z.number().int().min(0),
    coolingOffDays: z.literal(7),
    previewHash: accountDeletionHashSchema,
    policyVersions: accountDeletionPolicyVersionsSchema,
    copy: accountDeletionPreviewCopySchema,
    valueFacts: accountDeletionValueFactsSchema,
    lifecycleFacts: accountDeletionLifecycleFactsSchema,
    executorReadiness: accountDeletionExecutorReadinessSchema,
    blockers: z.array(accountDeletionBlockerSchema).max(40),
    canRequest: z.boolean(),
    canCancel: z.boolean(),
});
export const accountDeletionRequestInputSchema = z.strictObject({
    expectedPreviewHash: accountDeletionHashSchema,
    idempotencyKey: accountDeletionIdempotencyKeySchema,
    valueDisposition: accountDeletionValueDispositionSchema.optional(),
    confirmation: z.strictObject({
        typed: z.literal("DELETE"),
        exportPromptAcknowledged: z.literal(true),
        immediateDeactivationAcknowledged: z.literal(true),
    }),
});
export const accountDeletionRequestSummarySchema = z.strictObject({
    requestId: accountDeletionUppercaseUlidSchema,
    status: accountDeletionRequestStatusSchema,
    requestedAt: z.number().int().min(0),
    executeAfter: z.number().int().min(0),
    cancelledAt: z.number().int().min(0).nullable(),
    completedAt: z.number().int().min(0).nullable(),
    canCancel: z.boolean(),
    valueDisposition: accountDeletionValueDispositionSchema.nullable(),
});
export const accountDeletionRequestResponseSchema = z.strictObject({
    schemaVersion: z.literal(accountDeletionSchemaVersion),
    request: accountDeletionRequestSummarySchema,
    replay: z.boolean(),
    sessionRevoked: z.boolean(),
    mcpGrantRevocation: z.enum(["attempted", "unavailable"]),
});
export const accountDeletionStatusResponseSchema = z.strictObject({
    schemaVersion: z.literal(accountDeletionSchemaVersion),
    authenticated: z.boolean(),
    request: accountDeletionRequestSummarySchema.nullable(),
});
export const accountDeletionCancelInputSchema = z.strictObject({
    requestId: accountDeletionUppercaseUlidSchema.optional(),
    idempotencyKey: accountDeletionIdempotencyKeySchema,
});
export const accountDeletionCancelResponseSchema = z.strictObject({
    schemaVersion: z.literal(accountDeletionSchemaVersion),
    request: accountDeletionRequestSummarySchema,
    replay: z.boolean(),
    sessionsRestored: z.literal(false),
    mcpGrantsRestored: z.literal(false),
    entitlementTimeExtended: z.literal(false),
});
export const accountDeletionStatusChallengeRequestInputSchema = z.strictObject({
    email: z.email().transform((value) => value.trim().toLowerCase()),
    locale: z.enum(["id", "en"]),
});
export const accountDeletionStatusChallengeRequestResponseSchema = z.strictObject({
    accepted: z.literal(true),
});
export const accountDeletionStatusChallengeVerifyInputSchema = z.strictObject({
    email: z.email().transform((value) => value.trim().toLowerCase()),
    token: z.string().trim().min(16).max(256),
});
export const accountDeletionStatusChallengeVerifyResponseSchema = z.strictObject({
    proofToken: z.string().min(16).max(256),
    expiresAt: z.number().int().min(0),
});
export const accountDeletionErrorCodeSchema = z.enum([
    "auth_unavailable",
    "unauthorized",
    "fresh_session_required",
    "forbidden",
    "invalid_request",
    "method_not_allowed",
    "payload_too_large",
    "stale_preview",
    "policy_stale",
    "value_disposition_required",
    "request_blocked",
    "executor_not_ready",
    "idempotency_conflict",
    "deletion_request_conflict",
    "deletion_status_unavailable",
    "invalid_proof",
]);
export const accountDeletionErrorResponseSchema = z.strictObject({
    error: z.strictObject({
        code: accountDeletionErrorCodeSchema,
        message: z.string().min(1).max(200),
        blockers: z.array(accountDeletionBlockerSchema).max(40).optional(),
    }),
});
//# sourceMappingURL=account-deletion.js.map